// src/pages/SellerIntelligence/Sales/SalesPerformance.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import styles from '../SellerIntelligence.module.css';
import { WindowControl, WindowField, useWindowControl } from '../components/WindowControl';
import KpiStrip from './components/KpiStrip';
import TrendChart from './components/TrendChart';
import TopSkus from './components/TopSkus';
import BottomSkus from './components/BottomSkus';
import RevenueByGroup from './components/RevenueByGroup';

// The Sales Performance Dashboard is one page. The PRD scopes its seven
// sub-features as internal engineering boundaries, so they are regions here
// rather than pages of their own, and there is no list or detail view —
// per-item analysis belongs to Product Performance Deep Dive.
//
// The window and granularity controls live on the page rather than inside any
// region, because they govern every component on it: the whole page always
// describes one consistent period. The trend chart consumes them today and each
// region built after it consumes the same pair.
//
// Every region is built. Category grouping inside the last one is still
// unavailable, and says so on the surface rather than being hidden.

const GRANULARITIES = ['day', 'week', 'month', 'quarter', 'year'];

const SalesPerformance = () => {
  const navigate = useNavigate();
  // Edited freely, applied on demand: every region refetches when the window
  // changes, so a half-typed date must not trigger a load.
  const control = useWindowControl();
  const [granularity, setGranularity] = useState('day');

  return (
    <div className={styles.featureContainer}>
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => navigate('/dashboard/seller-intelligence/sales')}
        >
          <FiArrowLeft /> Back
        </button>
        <div className={styles.titleSection}>
          <div className={styles.iconTitle}><h1>Sales Performance Dashboard</h1></div>
          <p>
            How the business is moving over any period, with returns plotted
            against sales and the products and brands behind the numbers. One
            page, no drill-down.
          </p>
        </div>
      </div>

      <div className={styles.content}>

        {/* Two shared controls govern every region below, so the whole page
            always describes one consistent period. The window control is a
            shared Seller Intelligence component and is not owned by this page. */}
        <WindowControl control={control}>
          <WindowField label="Granularity">
            <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
              {GRANULARITIES.map((option) => (
                <option key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))}
            </select>
          </WindowField>
        </WindowControl>


        <KpiStrip window={control.applied} onLoadingChange={control.track('kpiStrip')} />

        <TrendChart window={control.applied} onLoadingChange={control.track('trendChart')} granularity={granularity} />

        <TopSkus window={control.applied} onLoadingChange={control.track('topSkus')} />

        <BottomSkus window={control.applied} onLoadingChange={control.track('bottomSkus')} />

        <RevenueByGroup window={control.applied} onLoadingChange={control.track('revenueByGroup')} />

      </div>
    </div>
  );
};

export default SalesPerformance;
